import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Bookmark, BookmarkCheck, CheckCircle2, HelpCircle, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import CheckoutDialog from "@/components/CheckoutDialog";
import CheckoutButton from "@/components/CheckoutButton";
import PromoCodeInput from "@/components/PromoCodeInput";
import CalculatorModal from "@/components/CalculatorModal";
import ReactMarkdown from "react-markdown";

// Defined at module scope so it isn't re-created on every render — see
// react/no-unstable-nested-components.
const DISCLAIMER_LINK = ({ node, ...props }) => (
    <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gold-600 underline underline-offset-2 hover:text-gold-700"
    />
);
const DISCLAIMER_ALLOWED = ["p", "a", "strong", "em", "code", "ul", "ol", "li", "br"];
import WorkflowRunner from "@/components/WorkflowRunner";
import { useAuth } from "@/lib/AuthContext";
import { useMembershipGate } from "@/hooks/useMembershipGate";
import { toast } from "sonner";

export default function DealDetail() {
    const { dealId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [deal, setDeal] = useState(null);
    const [saved, setSaved] = useState(false);
    const [open, setOpen] = useState(false);
    const [promo, setPromo] = useState("");
    const [calcOpen, setCalcOpen] = useState(false);
    const [calcBusy, setCalcBusy] = useState(false);
    const [referralBusy, setReferralBusy] = useState(false);
    const [wfOpen, setWfOpen] = useState(false);
    const [wfInitialStep, setWfInitialStep] = useState(undefined);
    const [wfExtras, setWfExtras] = useState(null);
    const [quote, setQuote] = useState(null);
    const { user } = useAuth();
    const { requireActiveMembership, MembershipGateDialog } = useMembershipGate();

    const hasWorkflow = Boolean(
        deal?.workflow &&
            (deal.workflow.splash_enabled ||
                deal.workflow.calculator_enabled ||
                deal.workflow.select_members_enabled ||
                deal.workflow.booking_enabled)
    );

    useEffect(() => {
        api.get(`/public/deals/${dealId}`).then((r) => setDeal(r.data));
        api.get("/member/saved").then((r) => {
            if (r.data.find((s) => s.id === dealId)) setSaved(true);
        });
    }, [dealId]);

    // Resume signal from the calculator page (`?resume=calculator`) — reopen
    // the purchase workflow past the calculator step (e.g. straight to
    // booking) instead of leaving the member on a fresh, unstarted page.
    // Strip the param either way so a later refresh doesn't reopen it.
    useEffect(() => {
        if (!deal) return;
        if (searchParams.get("resume") === "calculator") {
            if (deal.workflow?.calculator_enabled) {
                setWfInitialStep("book");
                setWfOpen(true);
            }
            const next = new URLSearchParams(searchParams);
            next.delete("resume");
            setSearchParams(next, { replace: true });
        }
    }, [deal]);

    const toggleSave = async () => {
        try {
            const { data } = await api.post("/member/save", { deal_id: dealId });
            setSaved(data.saved);
            toast.success(data.saved ? "Saved to your list" : "Removed from saved");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    if (!deal) return <div className="p-10 text-navy-500">Loading…</div>;

    return (
        <>
            <div className="px-6 lg:px-12 pt-8">
                <Link
                    to="/member/deals"
                    data-testid="deal-back"
                    className="inline-flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700"
                >
                    <ArrowLeft size={15} /> Back to my deals
                </Link>
            </div>

            <div className="px-6 lg:px-12 py-8 grid lg:grid-cols-[1fr_400px] gap-10 items-start">
                <div>
                    <div className="rounded-2xl bg-white shadow-soft overflow-hidden">
                        <div className="h-80 flex items-center justify-center pt-6">
                            <img
                                src={deal.image}
                                alt={deal.title}
                                className="h-64 object-contain"
                            />
                        </div>
                        <div className="p-8">
                            <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                                {deal.vendor} · {deal.tag}
                            </div>
                            <h1 className="font-display text-4xl text-navy-700 mb-4">
                                {deal.title}
                            </h1>
                            <div className="flex items-center gap-3 mb-6 text-sm text-navy-500">
                                <img src="/pink_5_stars_large.png" alt="" className="h-3.5" />
                                {deal.rating.toFixed(1)} · {deal.reviews} member reviews
                            </div>
                            <p className="text-navy-500 leading-relaxed mb-6">
                                {deal.description}
                            </p>
                            <div className="space-y-3">
                                {deal.highlights.map((h) => (
                                    <div
                                        key={h}
                                        className="flex items-start gap-3 text-navy-700"
                                    >
                                        <CheckCircle2
                                            size={18}
                                            className="text-gold-600 mt-0.5 flex-shrink-0"
                                        />
                                        <span>{h}</span>
                                    </div>
                                ))}
                            </div>

                            {deal.good_to_know && deal.good_to_know.length > 0 && (
                                <div className="mt-10 pt-8 border-t border-navy-100/70">
                                    <div className="flex items-center gap-2 mb-4">
                                        <HelpCircle size={18} className="text-gold-600" />
                                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600">
                                            Good to know
                                        </div>
                                    </div>
                                    <Accordion
                                        type="single"
                                        collapsible
                                        className="w-full"
                                        data-testid="deal-good-to-know"
                                    >
                                        {deal.good_to_know.map((item, i) => (
                                            <AccordionItem
                                                key={`gtk-${item.q}-${i}`}
                                                value={`gtk-${i}`}
                                                data-testid={`deal-gtk-item-${i}`}
                                                className="border-navy-100/70"
                                            >
                                                <AccordionTrigger className="text-left text-navy-700 font-medium hover:no-underline hover:text-gold-600 py-4">
                                                    {item.q}
                                                </AccordionTrigger>
                                                <AccordionContent className="text-navy-500 leading-relaxed pb-4">
                                                    {item.a}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </div>
                            )}

                            {deal.disclaimer && (
                                <div
                                    data-testid="deal-disclaimer"
                                    className="mt-10 pt-6 border-t border-navy-100/70"
                                >
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-navy-400 mb-2">
                                        Disclaimer
                                    </div>
                                    <div className="text-xs text-navy-500 leading-relaxed prose prose-xs max-w-none">
                                        <ReactMarkdown
                                            components={{ a: DISCLAIMER_LINK }}
                                            allowedElements={DISCLAIMER_ALLOWED}
                                            unwrapDisallowed
                                        >
                                            {deal.disclaimer}
                                        </ReactMarkdown>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <aside className="lg:sticky lg:top-8">
                    <div className="rounded-2xl bg-white shadow-lift p-8">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                            Member price
                        </div>
                        {deal.hide_price_until_calculated && quote == null ? (
                            <div
                                data-testid="deal-price-hidden"
                                className="mb-2 text-navy-500"
                            >
                                Run the calculator below for your indicative price.
                            </div>
                        ) : deal.hide_price_until_calculated ? (
                            <div className="flex items-baseline gap-3 mb-2">
                                <div
                                    data-testid="deal-price-quoted"
                                    className="font-display text-5xl text-navy-700"
                                >
                                    {deal.is_paid_USD ? "US$" : "$"}{quote.toFixed(2)}
                                </div>
                                <div className="text-xs text-navy-400">indicative quote</div>
                            </div>
                        ) : (
                            <div className="flex items-baseline gap-3 mb-2">
                                <div className="font-display text-5xl text-navy-700">
                                    {deal.is_paid_USD ? "US$" : "$"}{deal.price.toFixed(2)}
                                </div>
                                {deal.original_price > deal.price && (
                                    <div className="text-navy-500 line-through">
                                        {deal.is_paid_USD ? "US$" : "$"}{deal.original_price.toFixed(2)}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="text-sm text-navy-500 mb-6">
                            You save <strong className="text-gold-600">{deal.savings_pct}%</strong> vs. public price.
                        </div>

                        {deal.payment_mode === "referral" ? (
                            <a
                                href={deal.referral_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="deal-referral-btn"
                                onClick={async (e) => {
                                    if (!requireActiveMembership()) {
                                        e.preventDefault();
                                        return;
                                    }
                                    // Raise a vendor case BEFORE the redirect so
                                    // the vendor gets an immediate email +
                                    // portal entry with the member's details.
                                    // Fire-and-forget: never block the redirect
                                    // on the case call failing.
                                    e.persist?.();
                                    setReferralBusy(true);
                                    try {
                                        await api.post("/member/vendor-cases", {
                                            deal_id: deal.id,
                                            case_type: "referral_click",
                                        });
                                    } catch (_e) {
                                        // swallow — case may be dupe; still allow redirect
                                    } finally {
                                        setReferralBusy(false);
                                    }
                                }}
                                className="w-full h-12 rounded-full bg-navy-700 hover:bg-navy-800 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                            >
                                {referralBusy ? "Preparing…" : `Continue on ${deal.vendor} site`}
                                <ExternalLink size={16} />
                            </a>
                        ) : (
                            <>
                                <PromoCodeInput
                                    context="deal"
                                    onApplied={setPromo}
                                    testidPrefix="deal-promo"
                                    className="mb-3"
                                />
                                {hasWorkflow ? (
                                    <>
                                        <Button
                                            type="button"
                                            onClick={() => setWfOpen(true)}
                                            data-testid="deal-workflow-start"
                                            className="w-full h-12 rounded-full bg-navy-700 hover:bg-navy-800 text-white font-semibold"
                                        >
                                            Start purchase workflow
                                        </Button>
                                        {wfExtras && (
                                            <CheckoutButton
                                                kind="deal"
                                                itemId={deal.id}
                                                customerEmail={user?.email}
                                                userId={user?.id}
                                                promoCode={promo || undefined}
                                                includeSubUserIds={
                                                    wfExtras.include_sub_user_ids
                                                }
                                                testid="deal-buy"
                                                className="hidden"
                                                autoStart
                                            >
                                                Buy this perk
                                            </CheckoutButton>
                                        )}
                                    </>
                                ) : (
                                    <CheckoutButton
                                        kind="deal"
                                        itemId={deal.id}
                                        customerEmail={user?.email}
                                        userId={user?.id}
                                        promoCode={promo || undefined}
                                        testid="deal-buy"
                                        className="w-full h-12 rounded-full bg-navy-700 hover:bg-navy-800 text-white font-semibold"
                                    >
                                        Buy this perk
                                    </CheckoutButton>
                                )}
                            </>
                        )}
                        {(deal.calculator || {}).enabled && (
                            <Button
                                type="button"
                                onClick={() => {
                                    const calc = deal.calculator || {};
                                    const src = calc.source_url;
                                    if (Array.isArray(calc.fields) && calc.fields.length > 0) {
                                        setCalcOpen(true);
                                    } else if (src && /^https?:\/\//i.test(src)) {
                                        window.open(src, "_blank", "noopener");
                                    } else {
                                        navigate(`/calculator?deal=${deal.id}`);
                                    }
                                }}
                                data-testid="deal-open-calculator"
                                variant="outline"
                                className="w-full h-11 mt-3 rounded-full border-gold-500 text-navy-700 hover:bg-gold-50 font-medium"
                            >
                                Get an indicative quote
                            </Button>
                        )}
                        <Button
                            data-testid="deal-save"
                            variant="outline"
                            onClick={toggleSave}
                            className="w-full h-11 mt-3 rounded-full border-navy-700 text-navy-700 hover:bg-navy-50"
                        >
                            {saved ? (
                                <>
                                    <BookmarkCheck size={16} className="mr-2 text-gold-600" />
                                    Saved
                                </>
                            ) : (
                                <>
                                    <Bookmark size={16} className="mr-2" />
                                    Save for later
                                </>
                            )}
                        </Button>

                        <div className="mt-6 text-xs text-navy-500 leading-relaxed">
                            Backed by the Panda Promise: we beat any public price for 30 days
                            or refund the difference.
                        </div>
                    </div>
                </aside>
            </div>

            <CheckoutDialog
                open={open}
                onOpenChange={setOpen}
                deal={deal}
                onSuccess={() => setTimeout(() => navigate("/member/payments"), 1500)}
            />
            <MembershipGateDialog />
            <CalculatorModal
                open={calcOpen}
                onClose={() => setCalcOpen(false)}
                config={
                    deal.is_paid_USD
                        ? { ...deal.calculator, currency: "USD" }
                        : deal.calculator
                }
                deal={deal}
                busy={calcBusy}
                onSubmit={async (inputs, calcQuote) => {
                    setCalcBusy(true);
                    try {
                        await api.post("/member/vendor-cases", {
                            deal_id: deal.id,
                            case_type: "calculator_quote",
                            calculator_inputs: inputs,
                            calculator_quote: calcQuote,
                        });
                        setQuote(calcQuote);
                        setCalcOpen(false);
                        toast.success(
                            `Sent to ${deal.vendor} — they'll follow up shortly.`
                        );
                    } catch (e) {
                        toast.error(formatApiError(e));
                    } finally {
                        setCalcBusy(false);
                    }
                }}
            />
            <WorkflowRunner
                open={wfOpen}
                onClose={() => {
                    setWfOpen(false);
                    setWfInitialStep(undefined);
                }}
                deal={deal}
                user={user}
                onCheckout={(extras) => setWfExtras(extras)}
                initialStep={wfInitialStep}
            />
        </>
    );
}
